// ─────────────────────────────────────────────────────────────────────────────
//  permissions.js
//  IoTMATE RBAC — Permission = Module + Action
//  Format: "module:action"
// ─────────────────────────────────────────────────────────────────────────────

// ── Permission sets per role ───────────────────────────────────────────────────
export const ROLE_PERMISSIONS = {

  "Super Admin": new Set([
    // Org
    "org:create", "org:edit", "org:suspend", "org:view_dashboard",
    "org:manage_subscription", "org:view_reports",
    // Site
    "site:create", "site:edit", "site:delete", "site:assign_solution",
    "site:assign_device", "site:view_dashboard", "site:configure_alerts",
    // Solution
    "solution:enable", "solution:configure_settings",
    "solution:view_dashboard", "solution:export_reports",
    // Device
    "device:add", "device:delete", "device:edit_metadata", "device:assign_to_site",
    "device:view_telemetry", "device:control", "device:firmware_update",
    "device:run_diagnostics", "device:change_network_config",
    // Alert
    "alert:view", "alert:acknowledge", "alert:resolve",
    "alert:configure_rules", "alert:escalate",
    // User
    "user:create", "user:invite", "user:assign_site", "user:change_role",
    "user:suspend", "user:view_activity_logs",
    // Report
    "report:view", "report:export_csv", "report:schedule", "report:api_access",
    // Security
    "security:reset_password", "security:enable_mfa",
    "security:generate_api_key", "security:view_audit_logs",
  ]),

  "Org Admin": new Set([
    // Org — manage own org
    "org:view_dashboard", "org:manage_subscription", "org:view_reports",
    // Site — full site management
    "site:create", "site:edit", "site:delete", "site:assign_solution",
    "site:assign_device", "site:view_dashboard", "site:configure_alerts",
    // Solution
    "solution:enable", "solution:configure_settings",
    "solution:view_dashboard", "solution:export_reports",
    // Device
    "device:add", "device:delete", "device:edit_metadata", "device:assign_to_site",
    "device:view_telemetry", "device:control", "device:firmware_update",
    // Alert
    "alert:view", "alert:acknowledge", "alert:resolve",
    "alert:configure_rules", "alert:escalate",
    // User — full user management within org
    "user:create", "user:invite", "user:assign_site", "user:change_role",
    "user:suspend", "user:view_activity_logs",
    // Report
    "report:view", "report:export_csv", "report:schedule",
    // Security
    "security:reset_password", "security:enable_mfa",
    "security:generate_api_key", "security:view_audit_logs",
  ]),

  "Org Manager": new Set([
    // Org — read only
    "org:view_dashboard", "org:view_reports",
    // Site — view only
    "site:view_dashboard",
    // Solution — view + export
    "solution:view_dashboard", "solution:export_reports",
    // Device — view telemetry
    "device:view_telemetry",
    // Alert — view only
    "alert:view",
    // User — view activity logs
    "user:view_activity_logs",
    // Report
    "report:view", "report:export_csv",
    // Security
    "security:enable_mfa",
  ]),

  "Site Admin": new Set([
    // Site — manage within site scope
    "site:edit", "site:assign_device", "site:view_dashboard", "site:configure_alerts",
    // Solution
    "solution:configure_settings", "solution:view_dashboard", "solution:export_reports",
    // Device
    "device:add", "device:edit_metadata", "device:assign_to_site",
    "device:view_telemetry", "device:control",
    // Alert
    "alert:view", "alert:acknowledge", "alert:resolve",
    "alert:configure_rules", "alert:escalate",
    // User — limited: assign sites only
    "user:assign_site",
    // Report
    "report:view", "report:export_csv",
    // Security
    "security:enable_mfa",
  ]),

  "Operator": new Set([
    "site:view_dashboard",
    "solution:view_dashboard",
    // Device — view + control
    "device:view_telemetry", "device:control",
    // Alert — view + acknowledge (no resolve)
    "alert:view", "alert:acknowledge",
    "report:view",
    "security:enable_mfa",
  ]),

  "Technician": new Set([
    "site:view_dashboard",
    "solution:view_dashboard",
    // Device — control + maintenance
    "device:view_telemetry", "device:control",
    "device:firmware_update", "device:run_diagnostics",
    // Alert — view + acknowledge + resolve
    "alert:view", "alert:acknowledge", "alert:resolve",
    "security:enable_mfa",
  ]),

  "Viewer": new Set([
    "org:view_reports",
    "site:view_dashboard",
    "solution:view_dashboard", "solution:export_reports",
    "device:view_telemetry",
    "alert:view",
    "report:view",
    "security:enable_mfa",
  ]),
};

// ── Core permission check ──────────────────────────────────────────────────────
/**
 * Check if a role has a specific permission.
 * @param {string} role       - Role name
 * @param {string} permission - "module:action" string
 * @returns {boolean}
 */
export function can(role, permission) {
  return ROLE_PERMISSIONS[role]?.has(permission) ?? false;
}

// ── Navigation visibility ──────────────────────────────────────────────────────

/** Organizations page — Super Admin only */
export const showOrgNav       = role => can(role, "org:create");

/** Sites page — Super Admin, Org Admin, Site Admin */
export const showSitesNav     = role => can(role, "site:create") || can(role, "site:edit");

/** Users page — roles that manage users */
export const showUsersNav     = role => can(role, "user:create") || can(role, "user:assign_site");

/** Org selector in TopNavBar — Super Admin only (sees all orgs) */
export const showOrgSelector  = role => can(role, "org:create");

/** Site selector — all roles */
export const showSiteSelector = () => true;

// ── Organization operations ────────────────────────────────────────────────────
export const canCreateOrg           = role => can(role, "org:create");
export const canEditOrg             = role => can(role, "org:edit");
export const canSuspendOrg          = role => can(role, "org:suspend");
export const canViewOrgDashboard    = role => can(role, "org:view_dashboard");
export const canManageSubscription  = role => can(role, "org:manage_subscription");
export const canViewOrgReports      = role => can(role, "org:view_reports");

// ── Site operations ────────────────────────────────────────────────────────────
export const canCreateSite          = role => can(role, "site:create");
export const canEditSite            = role => can(role, "site:edit");
export const canDeleteSite          = role => can(role, "site:delete");
export const canAssignSolution      = role => can(role, "site:assign_solution");
export const canAssignDeviceToSite  = role => can(role, "site:assign_device");
export const canConfigureSiteAlerts = role => can(role, "site:configure_alerts");

// ── Solution operations ────────────────────────────────────────────────────────
export const canEnableSolution        = role => can(role, "solution:enable");
export const canConfigureSolution     = role => can(role, "solution:configure_settings");
export const canExportSolutionReports = role => can(role, "solution:export_reports");

// ── Device operations ──────────────────────────────────────────────────────────
export const canAddDevice           = role => can(role, "device:add");
export const canDeleteDevice        = role => can(role, "device:delete");
export const canEditDeviceMetadata  = role => can(role, "device:edit_metadata");
export const canControlDevice       = role => can(role, "device:control");
export const canFirmwareUpdate      = role => can(role, "device:firmware_update");
export const canRunDiagnostics      = role => can(role, "device:run_diagnostics");
export const canChangeNetworkConfig = role => can(role, "device:change_network_config");

// ── Alert operations ───────────────────────────────────────────────────────────
export const canAcknowledgeAlert    = role => can(role, "alert:acknowledge");
export const canResolveAlert        = role => can(role, "alert:resolve");
export const canConfigureAlertRules = role => can(role, "alert:configure_rules");
export const canEscalateAlert       = role => can(role, "alert:escalate");

// ── User management operations ─────────────────────────────────────────────────
export const canCreateUser          = role => can(role, "user:create");
export const canInviteUser          = role => can(role, "user:invite");
export const canAssignSiteToUser    = role => can(role, "user:assign_site");
export const canChangeRole          = role => can(role, "user:change_role");
export const canSuspendUser         = role => can(role, "user:suspend");
export const canViewActivityLogs    = role => can(role, "user:view_activity_logs");

// ── Report operations ──────────────────────────────────────────────────────────
export const canViewReports         = role => can(role, "report:view");
export const canExportCSV           = role => can(role, "report:export_csv");
export const canScheduleReports     = role => can(role, "report:schedule");
export const canAPIAccess           = role => can(role, "report:api_access");

// ── Security operations ────────────────────────────────────────────────────────
export const canViewAuditLogs       = role => can(role, "security:view_audit_logs");
export const canGenerateAPIKey      = role => can(role, "security:generate_api_key");
export const canResetPassword       = role => can(role, "security:reset_password");
